import { z } from 'zod';

// src/enums.ts
var LocaleSchema = z.enum(["en", "pt-PT", "es", "fr"]);
var ChannelSchema = z.enum(["in_app", "telegram", "whatsapp_link", "whatsapp_cloud"]);
var MessageDirectionSchema = z.enum(["inbound", "outbound"]);
var DeliveryStateSchema = z.enum(["sent", "delivered", "read", "failed"]);
var PlaceStatusSchema = z.enum(["draft", "owner_approved", "published", "archived"]);
var GuesthouseStatusSchema = z.enum(["active", "archived"]);
var ReservationStatusSchema = z.enum([
  "confirmed",
  "checked_in",
  "checked_out",
  "cancelled"
]);
var TourPlanStatusSchema = z.enum([
  "pending",
  "retrieving",
  "planning",
  "validating",
  "completed",
  "failed"
]);
var TourSlotSchema = z.enum([
  "breakfast",
  "morning",
  "lunch",
  "afternoon",
  "dinner",
  "evening"
]);
var OwnerScopeSchema = z.enum(["guesthouse", "owner", "guest", "system"]);
var MediaKindSchema = z.enum(["image", "video"]);
var UuidSchema = z.guid();
var IsoDateTimeSchema = z.string().datetime({ offset: true });
var IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
var GeomSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180)
});
var UrlSchema = z.string().url();
var EmailSchema = z.string().email().toLowerCase();
var PhoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/);
var SlugSchema = z.string().regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/);
var VERSION = "0.1.0";
var I18nTextSchema = z.partialRecord(LocaleSchema, z.string()).refine((val) => Object.keys(val).length > 0, {
  message: "I18nText must have at least one locale entry"
});
function pickLocale(text, preferred, fallback = "en") {
  const map = text;
  const exact = map[preferred];
  if (exact) return exact;
  const base = preferred.toLowerCase().split("-")[0] ?? "";
  if (base) {
    for (const key of Object.keys(map)) {
      const k = key.toLowerCase();
      if (k === base || k.startsWith(`${base}-`)) {
        const hit = map[key];
        if (hit) return hit;
      }
    }
  }
  for (const candidate of typeof fallback === "string" ? [fallback] : fallback) {
    const hit = map[candidate];
    if (hit) return hit;
  }
  return Object.values(map).find((v) => typeof v === "string") ?? "";
}
var ReservationSchema = z.object({
  id: UuidSchema,
  guesthouse_id: UuidSchema,
  guest_id: UuidSchema,
  checkin: IsoDateSchema,
  checkout: IsoDateSchema,
  party_size: z.number().int().positive(),
  locale: LocaleSchema,
  status: ReservationStatusSchema,
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema
}).strict();
var GuestSchema = z.object({
  id: UuidSchema,
  display_name: z.string().min(1).max(200),
  locale: LocaleSchema,
  opt_in_flags: z.object({
    marketing: z.boolean().default(false),
    analytics: z.boolean().default(false)
  }),
  created_at: IsoDateTimeSchema
}).strict();
var GuesthouseSchema = z.object({
  id: UuidSchema,
  owner_id: UuidSchema,
  name: I18nTextSchema,
  slug: SlugSchema,
  address: z.string(),
  geom: GeomSchema,
  media: z.array(z.guid()),
  // Lifecycle status — active guesthouses are shown; archived are retired.
  status: GuesthouseStatusSchema,
  // Number of rooms (nº de quartos). Null when unknown.
  rooms: z.number().int().positive().nullable(),
  // Plan-006 6.A: global place_ids this guesthouse hides from its guests (opt-out scoping).
  hidden_place_ids: z.array(z.guid()),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema
}).strict();
var OwnerProfileSchema = z.object({
  owner_id: UuidSchema,
  bio: I18nTextSchema.optional(),
  photo: z.guid().optional(),
  phone: PhoneSchema.optional(),
  call_enabled: z.boolean().default(false),
  dm_channels: z.object({
    in_app: z.boolean(),
    telegram: z.boolean(),
    whatsapp_link: z.boolean(),
    whatsapp_cloud: z.boolean()
  }),
  email: EmailSchema.optional(),
  updated_at: IsoDateTimeSchema
}).strict();
var ActionSchema = z.object({
  id: UuidSchema,
  slug: SlugSchema,
  i18n: I18nTextSchema,
  sort_order: z.number().int().nonnegative(),
  icon: z.string()
}).strict();
var WishSchema = z.object({
  id: UuidSchema,
  action_id: UuidSchema,
  slug: SlugSchema,
  i18n: I18nTextSchema,
  sort_order: z.number().int().nonnegative()
}).strict();
var PlaceSchema = z.object({
  id: UuidSchema,
  guesthouse_scope: z.union([z.literal("all"), z.array(z.guid())]),
  name: I18nTextSchema,
  description: I18nTextSchema,
  geom: GeomSchema,
  address: z.string(),
  contacts: z.object({
    phone: PhoneSchema.optional(),
    email: EmailSchema.optional(),
    website: UrlSchema.optional(),
    social: z.array(
      z.object({
        kind: z.enum(["instagram", "facebook", "tiktok", "whatsapp", "telegram", "other"]),
        handle: z.string()
      })
    ).default([])
  }),
  hours: z.array(
    z.object({
      dow: z.number().int().min(0).max(6),
      open: z.string().regex(/^\d{2}:\d{2}$/),
      close: z.string().regex(/^\d{2}:\d{2}$/)
    })
  ).default([]),
  // Seasonal availability. null/absent = year-round (the common case);
  // "summer"/"winter" mark places that only operate in one season.
  season: z.enum(["summer", "winter"]).nullable().optional(),
  actions: z.array(z.guid()).min(1),
  wishes: z.array(z.guid()).default([]),
  media: z.array(z.guid()),
  status: PlaceStatusSchema,
  is_hosts_pick: z.boolean().default(false),
  source: z.object({
    kind: z.enum(["manual", "google_places", "osm", "crawl"]),
    ref: z.string().optional(),
    retrieved_at: IsoDateTimeSchema.optional()
  }),
  created_at: IsoDateTimeSchema,
  updated_at: IsoDateTimeSchema
}).strict();
var PlaceCandidateSchema = z.object({
  id: UuidSchema,
  source: z.enum(["google_places", "osm", "crawl", "manual"]),
  source_ref: z.string(),
  raw: z.record(z.string(), z.unknown()),
  dedupe_hash: z.string(),
  status: z.enum(["new", "reviewed", "approved", "rejected"]),
  proposed_place: PlaceSchema.partial().optional(),
  created_at: IsoDateTimeSchema
}).strict();
var VariantKeySchema = z.string().regex(/^\d+w_(avif|webp)$/);
var MediaAssetSchema = z.object({
  id: UuidSchema,
  bucket_key: z.string(),
  mime: z.string(),
  kind: MediaKindSchema,
  dims: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive()
  }).optional(),
  bytes: z.number().int().positive(),
  owner_scope: OwnerScopeSchema,
  owner_id: z.guid(),
  // dt-tests #37 — the declared shape was `{key,width,height,mime}` objects
  // while the transcode worker has always written plain bucket-key STRINGS
  // (workers/transcode.ts: `variants[`${size}w_${fmt}`] = key`). The stored
  // qual data is strings. Nothing ever threw, because this schema was never
  // wired to a boundary — a shape mismatch that cannot fire is itself
  // evidence that nothing consumed the output.
  //
  // The contract now matches reality rather than the reverse: the key name
  // already carries both facts a consumer needs (width and format), so the
  // richer object bought nothing and would have cost a data migration.
  variants: z.record(VariantKeySchema, z.string()).default({}),
  created_at: IsoDateTimeSchema
}).strict();
var ChatThreadSchema = z.object({
  id: UuidSchema,
  reservation_id: UuidSchema,
  owner_id: UuidSchema,
  created_at: IsoDateTimeSchema,
  last_message_at: IsoDateTimeSchema.optional()
}).strict();
var CHAT_JWT_SUBPROTOCOL = "dt.jwt";
var MessageSchema = z.object({
  id: UuidSchema,
  thread_id: UuidSchema,
  direction: MessageDirectionSchema,
  channel: ChannelSchema,
  ext_ref: z.string().optional(),
  body: z.string(),
  attachments: z.array(z.guid()).default([]),
  delivery_state: DeliveryStateSchema,
  ts: IsoDateTimeSchema
}).strict();
var ChannelBindingSchema = z.object({
  id: UuidSchema,
  thread_id: UuidSchema,
  channel: ChannelSchema,
  ext_chat_id: z.string(),
  verified_at: IsoDateTimeSchema.optional()
}).strict();
var TourStepSchema = z.object({
  slot: TourSlotSchema,
  place_id: UuidSchema,
  start: IsoDateTimeSchema,
  end: IsoDateTimeSchema,
  rationale: z.string(),
  travel_to_minutes: z.number().int().nonnegative().optional(),
  locked: z.boolean().default(false)
}).strict();
var TourPlanSchema = z.object({
  id: UuidSchema,
  reservation_id: UuidSchema,
  params: z.object({
    date: IsoDateSchema,
    start_time: z.string().regex(/^\d{2}:\d{2}$/),
    end_time: z.string().regex(/^\d{2}:\d{2}$/),
    party_size: z.number().int().positive(),
    vehicle: z.enum(["car", "none"]),
    notes: z.string().optional()
  }),
  status: TourPlanStatusSchema,
  steps: z.array(TourStepSchema),
  weather_aware: z.boolean().default(false),
  llm_cost_usd: z.number().nonnegative().optional(),
  created_at: IsoDateTimeSchema,
  completed_at: IsoDateTimeSchema.optional()
}).strict();
var TokenGrantSchema = z.object({
  jti: z.string(),
  reservation_id: UuidSchema,
  issued_at: IsoDateTimeSchema,
  expires_at: IsoDateTimeSchema,
  revoked_at: IsoDateTimeSchema.optional()
}).strict();
var JTI_REVOKED_PREFIX = "jti:revoked:";
function jtiRevokedKey(jti) {
  return `${JTI_REVOKED_PREFIX}${jti}`;
}
var GUEST_JWT_MAX_TTL_SECONDS = 60 * 60;

export { ActionSchema, CHAT_JWT_SUBPROTOCOL, ChannelBindingSchema, ChannelSchema, ChatThreadSchema, DeliveryStateSchema, EmailSchema, GUEST_JWT_MAX_TTL_SECONDS, GeomSchema, GuestSchema, GuesthouseSchema, GuesthouseStatusSchema, I18nTextSchema, IsoDateSchema, IsoDateTimeSchema, JTI_REVOKED_PREFIX, LocaleSchema, MediaAssetSchema, MediaKindSchema, MessageDirectionSchema, MessageSchema, OwnerProfileSchema, OwnerScopeSchema, PhoneSchema, PlaceCandidateSchema, PlaceSchema, PlaceStatusSchema, ReservationSchema, ReservationStatusSchema, SlugSchema, TokenGrantSchema, TourPlanSchema, TourPlanStatusSchema, TourSlotSchema, TourStepSchema, UrlSchema, UuidSchema, VERSION, VariantKeySchema, WishSchema, jtiRevokedKey, pickLocale };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map